import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  const categoryArticles = currentCategory
    ? articles.filter((a) => a.categorySlug === currentCategory.slug)
    : [];

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 萝莉风格时尚穿搭指南`
    : "分类未找到 - 萝莉风格时尚穿搭指南";
  const pageDesc = currentCategory
    ? currentCategory.description
    : "探索萝莉风格的时尚世界";

  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory?.name || "萝莉"},萝莉风格穿搭,洛丽塔时尚,甜美系穿搭`}
        canonicalUrl={`https://lolitastyle.cn/category/${slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20collage%20with%20pastel%20dresses%20lace%20ribbons%2C%20soft%20pink%20aesthetic%2C%20editorial%20fashion%20design&width=1200&height=630&seq=og-category&orientation=landscape"
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: currentCategory?.name || "分类" },
          ]}
        />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              {currentCategory?.name || "分类未找到"}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {currentCategory?.description || "该分类不存在，请返回首页浏览其他内容。"}
            </p>
          </div>
        </section>

        {/* Content Area: Sidebar + Article List */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Category Switcher */}
            <aside className="w-full lg:w-56 flex-shrink-0">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">全部分类</h3>
              <nav className="flex flex-row lg:flex-col flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      cat.slug === slug
                        ? "bg-primary-100 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1.5 text-xs text-foreground-400">({cat.articleCount})</span>
                  </Link>
                ))}
              </nav>
            </aside>

            {/* Article List */}
            <div className="flex-1 min-w-0">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {article.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-xs text-foreground-400 mt-3">
                          <span>{article.author}</span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-500 mb-4">该分类暂无文章内容</p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Categories */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              探索其他分类
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .slice(0, 4)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="p-4 rounded-lg bg-background-50 text-center hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <span className="block text-sm font-medium text-foreground-950 mb-1">{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.articleCount} 篇文章</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}